
  import { Vue, Component, Prop } from "vue-property-decorator";
  import Tooltip from "@/components/common/Tooltip.vue";
  import ColorCodeTriangle from "@/components/scheduler/ColorCodeTriangle.vue";

  import ManagerViewProjectItem from "@/entities/scheduler/ManagerViewProjectItem";
  import SchedulerItemAvatar from "@/components/scheduler/SchedulerItemAvatar.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import { Project, Project_Activity, User } from "@/generated/graphql";
  import getFullName from "@/helpers/getFullName";
  import getAvatarPadding from "@/helpers/scheduler/getAvatarPadding";
  import TooltipVariants from "@/entities/TooltipVariants";
  import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";

  @Component({
    components: {
      Tooltip,
      ColorCodeTriangle,
      SchedulerItemAvatar,
      UiButton,
    }
  })
  export default class ProjectItem extends Vue {
    @Prop({ type: Object, required: true }) readonly project!: ManagerViewProjectItem;

    TooltipVariants = TooltipVariants;

    get projectItem(): ProjectDataForModal {
      return this.project.project;
    }

    get projectActivities(): Project_Activity[] {
      return this.projectItem?.project_activities || [];
    }

    get projectName(): string {
      return this.projectItem?.name! || "";
    }

    get notConfirmedHoursExist(): boolean {
      return Boolean(this.projectActivities.filter(activity => {
        return Boolean(activity.requested_hours && !activity.confirmed_hours);
      })?.length);
    }

    get contractors(): User[] {
      if (!this.projectItem || !this.projectItem.project_activities) {
        return [];
      }
      const contractorIdSet = new Set();
      return this.projectItem.project_activities
        .filter(activity => {
          if (!Boolean(activity.contractor) || activity?.contractor?.deleted) {
            return false;
          }
          if (contractorIdSet.has(activity.contractor!.id)) {
          return false;
          }
          contractorIdSet.add(activity.contractor!.id);
          return true;
          })
        .map(({contractor}) => {
          return contractor!.user;
        });
    }

    get contractorsExist(): boolean {
      return Boolean(this.contractors.length);
    }

    get projectActivitiesNumber(): number {
      return this.projectActivities.length;
    }

    get contractorsMissing(): boolean {
      if (!this.projectItem || !this.projectItem.project_activities) {
        return false;
      }
      const activitiesWithContractors = this.projectItem.project_activities.filter(activity => {
        return Boolean(activity.contractor);
      })
      return this.projectItem.project_activities.length !== activitiesWithContractors.length;
    }

    get colorCode(): string {
      if (this.contractorsMissing) {
        return "danger";
      }
      if (this.notConfirmedHoursExist) {
        return "warning";
      }
      return "";
    }

    get tooltipTitle(): string {
      const contractorsMissingMsg: string = "One or more project tasks do not have contractors";
      const hoursNeedConfirmingMsg: string = "one or more project tasks has requested hours that need confirming";
      if (this.contractorsMissing && this.notConfirmedHoursExist) {
        return `${contractorsMissingMsg} and ${hoursNeedConfirmingMsg}`;
      }
      if (this.contractorsMissing) {
        return contractorsMissingMsg;
      }
      if (this.notConfirmedHoursExist) {
        return hoursNeedConfirmingMsg.substring(0, 1).toUpperCase() + hoursNeedConfirmingMsg.substring(1);
      }
      return "";
    }

    get avatarsPadding(): string {
      return getAvatarPadding(this.colorCode);
    }

    getContractorName(contractor: User): string {
      if (!contractor) {
        return "";
      }
      return getFullName(contractor.given_name!, contractor.family_name!)
    }
  }
